import { useEffect, useState } from "react";
import { z } from "zod";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import {
  REQUEST_TYPE_LABELS,
  SUPPORT_EMAIL,
  type SupportRequestType,
} from "@/config/contact";
import { checkReviewQuality, checkFieldQuality } from "@/lib/gibberishFilter";

const schema = z.object({
  submitterName: z.string().trim().max(120).optional(),
  submitterEmail: z.string().trim().email("Enter a valid email").max(255),
  subjectLine: z.string().trim().max(200).optional(),
  message: z
    .string()
    .trim()
    .min(10, "Please give us a few more details (10+ chars).")
    .max(5000, "Please keep messages under 5,000 characters."),
});

interface Props {
  requestType: SupportRequestType;
  reviewId?: string;
  contentId?: string;
  defaultSubject?: string;
  showNameField?: boolean;
  submitLabel?: string;
  onSubmitted?: () => void;
}

const SupportRequestForm = ({
  requestType,
  reviewId,
  contentId,
  defaultSubject,
  showNameField = true,
  submitLabel = "Send to support",
  onSubmitted,
}: Props) => {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [subject, setSubject] = useState(defaultSubject ?? "");
  const [message, setMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [userId, setUserId] = useState<string | null>(null);

  useEffect(() => {
    supabase.auth.getUser().then(({ data }) => {
      setUserId(data.user?.id ?? null);
      if (data.user?.email) setEmail((prev) => prev || data.user!.email!);
    });
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const parsed = schema.safeParse({
      submitterName: name,
      submitterEmail: email,
      subjectLine: subject,
      message,
    });
    if (!parsed.success) {
      const first = parsed.error.errors[0];
      toast({ title: "Please check the form", description: first.message, variant: "destructive" });
      return;
    }

    for (const [val, label] of [
      [parsed.data.submitterName || "", "Name"],
      [parsed.data.subjectLine || "", "Subject"],
    ] as const) {
      const fq = checkFieldQuality(val, label);
      if (!fq.ok) {
        toast({ title: "Please improve your submission", description: fq.reason, variant: "destructive" });
        return;
      }
    }
    const mq = checkReviewQuality(parsed.data.message);
    if (!mq.ok) {
      toast({ title: "Please improve your message", description: mq.reason, variant: "destructive" });
      return;
    }


    setSubmitting(true);
    const timestamp = new Date().toISOString();
    const idempotencyKey = `support-${requestType}-${timestamp}-${Math.random().toString(36).slice(2, 8)}`;

    try {
      const { data, error } = await supabase.functions.invoke("send-transactional-email", {
        body: {
          templateName: "support-request",
          recipientEmail: SUPPORT_EMAIL,
          idempotencyKey,
          templateData: {
            requestType,
            requestTypeLabel: REQUEST_TYPE_LABELS[requestType],
            userId,
            userEmail: parsed.data.submitterEmail,
            submitterEmail: parsed.data.submitterEmail,
            submitterName: parsed.data.submitterName || null,
            reviewId: reviewId ?? null,
            contentId: contentId ?? null,
            page: typeof window !== "undefined" ? window.location.pathname : null,
            subjectLine: parsed.data.subjectLine || null,
            message: parsed.data.message,
            timestamp,
            userAgent: typeof navigator !== "undefined" ? navigator.userAgent : null,
          },
        },
      });

      if (error || (data as any)?.error) {
        const errMsg = error?.message || (data as any)?.error || "Unknown send error";
        // Log delivery failure to the email_send_log table so it shows up
        // alongside backend send failures.
        console.error("[support-request] send failed", { requestType, errMsg });
        await supabase.from("email_send_log" as any).insert({
          message_id: idempotencyKey,
          template_name: "support-request",
          recipient_email: SUPPORT_EMAIL,
          status: "failed",
          error_message: String(errMsg).slice(0, 1000),
        } as any);
        toast({
          title: "Couldn't send your message",
          description: `Please email ${SUPPORT_EMAIL} directly.`,
          variant: "destructive",
        });
        return;
      }

      toast({
        title: "Sent",
        description: `Our team will reply from ${SUPPORT_EMAIL}.`,
      });
      setSubject(defaultSubject ?? "");
      setMessage("");
      onSubmitted?.();
    } catch (err: any) {
      console.error("[support-request] unexpected", err);
      await supabase.from("email_send_log" as any).insert({
        message_id: idempotencyKey,
        template_name: "support-request",
        recipient_email: SUPPORT_EMAIL,
        status: "failed",
        error_message: String(err?.message ?? err).slice(0, 1000),
      } as any);
      toast({
        title: "Couldn't send your message",
        description: `Please email ${SUPPORT_EMAIL} directly.`,
        variant: "destructive",
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {showNameField && (
        <div className="space-y-1.5">
          <Label htmlFor="sr-name">Your name (optional)</Label>
          <Input id="sr-name" value={name} onChange={(e) => setName(e.target.value)} maxLength={120} />
        </div>
      )}
      <div className="space-y-1.5">
        <Label htmlFor="sr-email">Email <span className="text-destructive">*</span></Label>
        <Input
          id="sr-email"
          type="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          maxLength={255}
        />
      </div>
      <div className="space-y-1.5">
        <Label htmlFor="sr-subject">Subject (optional)</Label>
        <Input
          id="sr-subject"
          value={subject}
          onChange={(e) => setSubject(e.target.value)}
          maxLength={200}
        />
      </div>
      <div className="space-y-1.5">
        <Label htmlFor="sr-message">Message <span className="text-destructive">*</span></Label>
        <Textarea
          id="sr-message"
          required
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          rows={6}
          maxLength={5000}
          placeholder="Tell us what's going on..."
        />
        <p className="text-xs text-muted-foreground">
          Submitted to {SUPPORT_EMAIL}. We include your user ID and timestamp to help us respond faster.
        </p>
      </div>
      <Button type="submit" disabled={submitting} className="w-full rounded-lg">
        {submitting ? "Sending..." : submitLabel}
      </Button>
    </form>
  );
};

export default SupportRequestForm;
